'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from 'react';
import popoverStyles from './nav-popover.module.css';
import { MenuItem } from '@/types/MenuItem';
import { usePathname } from 'next/navigation';
import LinkTransition from './link-transition';
import { cn } from '@/lib/utils';
import { usePrefersReducedTransparency } from '@/hooks/usePrefersReducedTransparency';
import { useTheme } from 'next-themes';
import { Themes } from '@/types/theme';
import { trackCronitorEvent } from '@/hooks/useCronitor';
import { useMounted } from '@/hooks/useMounted';
import { MdLightMode, MdDarkMode, MdAutoMode, MdCheck } from 'react-icons/md';
type MenuProps = {
  items: MenuItem[];
  spacing?: boolean;
  noBackground?: boolean;
};
type NavLinkProps = {
  item: MenuItem;
  isActive: boolean;
  mobile?: boolean;
};
const THEME_OPTIONS = [{
  value: Themes.LIGHT,
  Icon: MdLightMode,
  label: 'Light'
}, {
  value: Themes.SYSTEM,
  Icon: MdAutoMode,
  label: 'System'
}, {
  value: Themes.DARK,
  Icon: MdDarkMode,
  label: 'Dark'
}] as const;
function ThemeIcon(t0) {
  const $ = _c(5);
  const {
    theme,
    size
  } = t0;
  let t1;
  if ($[0] !== theme) {
    t1 = THEME_OPTIONS.find(o => o.value === theme) ?? THEME_OPTIONS[1];
    $[0] = theme;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const opt = t1;
  let t2;
  if ($[2] !== opt.Icon || $[3] !== size) {
    t2 = <opt.Icon size={size} />;
    $[2] = opt.Icon;
    $[3] = size;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// -------------------------------------------------------------------
// ThemeDropdown — a compact trigger button + floating dropdown panel
// placement='bottom' for desktop (nav is at the top)
// placement='top'    for mobile  (nav is at the bottom)
// -------------------------------------------------------------------
type ThemeDropdownProps = {
  theme: Themes;
  onSelect: (t: Themes) => void;
  placement?: 'top' | 'bottom';
  triggerClassName?: string;
};
function ThemeDropdown(t0) {
  const $ = _c(23);
  const {
    theme,
    onSelect,
    placement: t1,
    triggerClassName
  } = t0;
  const placement = t1 === undefined ? "bottom" : t1;
  const panelRef = useRef(null);
  const isTop = placement === "top";
  const popoverId = `theme-popover-${placement}`;
  const t2 = popoverStyles[isTop ? "triggerMobile" : "triggerDesktop"];
  let t3;
  if ($[0] !== t2 || $[1] !== triggerClassName) {
    t3 = cn(t2, "text-content-text flex min-w-0 items-center justify-center rounded-full border-0 bg-black/6 p-0 transition-colors duration-150 hover:bg-black/11 focus-visible:ring-2 focus-visible:ring-black/20 focus-visible:outline-hidden dark:bg-white/8 dark:hover:bg-white/[0.14] dark:focus-visible:ring-white/20", triggerClassName);
    $[0] = t2;
    $[1] = triggerClassName;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== theme) {
    t4 = <ThemeIcon theme={theme} size={18} />;
    $[3] = theme;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== popoverId || $[6] !== t3 || $[7] !== t4) {
    t5 = <button type="button" popoverTarget={popoverId} aria-haspopup="listbox" aria-label="Theme" className={t3}>{t4}</button>;
    $[5] = popoverId;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = isTop ? popoverStyles.panelTop : popoverStyles.panelBottom;
  let t7;
  if ($[9] !== t6) {
    t7 = cn("min-w-[148px] overflow-hidden rounded-xl border border-border-color bg-(--bg) shadow-medium dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)]", popoverStyles.panel, t6);
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onSelect || $[12] !== theme) {
    t8 = THEME_OPTIONS.map(t9 => {
      const {
        value,
        Icon,
        label
      } = t9;
      const isActive = theme === value;
      return <button key={value} role="option" aria-selected={isActive} type="button" onClick={() => {
        if (!isActive) {
          trackCronitorEvent("ThemeChange", {
            message: value
          });
          onSelect(value);
        }
        panelRef.current?.hidePopover?.();
      }} className={cn("flex w-full items-center gap-2.5 rounded-lg border border-transparent bg-transparent px-2.5 py-2 text-sm transition-colors duration-100", isActive ? "bg-black/6 font-semibold text-black dark:bg-white/12 dark:text-white" : "font-medium text-black/50 hover:border-black/[0.07] hover:bg-black/5 hover:text-black dark:text-white/50 dark:hover:border-white/8 dark:hover:bg-white/8 dark:hover:text-white")}><Icon size={16} /><span className="flex-1 text-left">{label}</span>{isActive && <MdCheck size={14} className="shrink-0 text-black/40 dark:text-white/50" />}</button>;
    });
    $[11] = onSelect;
    $[12] = theme;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className="flex flex-col gap-0.5 p-1">{t8}</div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== popoverId || $[17] !== t7 || $[18] !== t9) {
    t10 = <div ref={panelRef} id={popoverId} popover="auto" role="listbox" aria-label="Theme" className={t7}>{t9}</div>;
    $[16] = popoverId;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t5) {
    t11 = <div className="relative">{t5}{t10}</div>;
    $[20] = t10;
    $[21] = t5;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}

// -------------------------------------------------------------------

function NavLink(t0) {
  const $ = _c(17);
  const {
    item,
    isActive,
    mobile: t1
  } = t0;
  const mobile = t1 === undefined ? false : t1;
  const t2 = mobile && "block";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("relative", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = item.link;
  const t5 = isActive ? "page" : undefined;
  let t6;
  if ($[2] !== isActive || $[3] !== mobile) {
    t6 = isActive ? {
      viewTransitionName: mobile ? "nav-active-mobile" : "nav-active"
    } : undefined;
    $[2] = isActive;
    $[3] = mobile;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = mobile ? "block w-full px-2 py-2 text-center text-[0.65rem] leading-tight tracking-[0.12em]" : "inline-flex h-9 min-w-28 items-center justify-center text-[0.82rem] tracking-[0.12em]";
  const t8 = isActive ? "bg-content-text text-text" : "hover:bg-content-text/15 text-content-text hover:text-content-text opacity-80 hover:opacity-100";
  let t9;
  if ($[5] !== t7 || $[6] !== t8) {
    t9 = cn("nav-pill relative rounded-full font-semibold uppercase transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-black/10 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-hidden dark:focus-visible:ring-white/25 dark:focus-visible:ring-offset-zinc-950", t7, t8);
    $[5] = t7;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== item.itemName || $[9] !== item.link || $[10] !== t5 || $[11] !== t6 || $[12] !== t9) {
    t10 = <LinkTransition href={t4} aria-current={t5} style={t6} className={t9}>{item.itemName}</LinkTransition>;
    $[8] = item.itemName;
    $[9] = item.link;
    $[10] = t5;
    $[11] = t6;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t3) {
    t11 = <span className={t3}>{t10}</span>;
    $[14] = t10;
    $[15] = t3;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}

// -------------------------------------------------------------------

const NavBar = t0 => {
  const $ = _c(52);
  const {
    items,
    spacing,
    noBackground: t1
  } = t0;
  const noBackground = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const reduceTransparency = usePrefersReducedTransparency();
  const {
    theme,
    setTheme
  } = useTheme();
  const mounted = useMounted();
  const [isMobileHidden, setIsMobileHidden] = useState(false);
  const lastScrollY = useRef(0);
  const scrollRaf = useRef(null);
  const shouldUseSolidShell = reduceTransparency || noBackground;
  const currentTheme = mounted ? theme as Themes ?? Themes.SYSTEM : Themes.SYSTEM;
  const handleGlowMove = _temp;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const onScroll = () => {
        if (scrollRaf.current !== null) {
          return;
        }
        scrollRaf.current = window.requestAnimationFrame(() => {
          scrollRaf.current = null;
          const currentY = window.scrollY;
          const delta = currentY - lastScrollY.current;
          if (Math.abs(delta) > 6) {
            setIsMobileHidden(delta > 0 && currentY > 80);
            lastScrollY.current = currentY;
          }
        });
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => {
        if (scrollRaf.current !== null) {
          cancelAnimationFrame(scrollRaf.current);
        }
        window.removeEventListener("scroll", onScroll);
      };
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== spacing) {
    t4 = spacing && <div className="mb-5 hidden h-16 md:block" />;
    $[2] = spacing;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      viewTransitionName: "site-nav"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = !noBackground && "border-border-color shadow-custom border";
  const t7 = shouldUseSolidShell && "nav-shell-solid";
  let t8;
  if ($[5] !== t6 || $[6] !== t7) {
    t8 = cn("nav-shell hidden items-center gap-1.5 rounded-full px-2 py-2 md:flex", t6, t7);
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== items || $[9] !== pathname) {
    let t10;
    if ($[11] !== pathname) {
      t10 = item => <NavLink key={item.link} item={item} isActive={pathname === item.link} />;
      $[11] = pathname;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    t9 = items.map(t10);
    $[8] = items;
    $[9] = pathname;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="bg-content-text/15 mx-0.5 h-4 w-px rounded-full" aria-hidden="true" />;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== currentTheme || $[15] !== setTheme) {
    t11 = <ThemeDropdown theme={currentTheme} onSelect={setTheme} placement="bottom" triggerClassName="h-9 w-9" />;
    $[14] = currentTheme;
    $[15] = setTheme;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t8 || $[19] !== t9) {
    t12 = <div className="text-content-text fixed top-0 z-40 w-full"><div className="mx-auto flex h-[74px] max-w-6xl items-center justify-end px-3 md:justify-center"><nav aria-label="Primary" style={t5} className={t8} onMouseMove={handleGlowMove}>{t9}{t10}{t11}</nav></div></div>;
    $[17] = t11;
    $[18] = t8;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = isMobileHidden ? "calc(-6rem - env(safe-area-inset-bottom))" : "1rem";
  let t14;
  if ($[21] !== t13) {
    t14 = {
      bottom: t13,
      paddingBottom: "env(safe-area-inset-bottom)"
    };
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = t14 as React.CSSProperties;
  const t16 = isMobileHidden ? "pointer-events-none" : "";
  let t17;
  if ($[23] !== t16) {
    t17 = cn("fixed right-0 left-0 z-50 mx-auto w-[min(98vw,680px)] transition-[bottom] duration-300 md:hidden", t16);
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== currentTheme || $[26] !== setTheme) {
    t18 = <div className="absolute -top-14 right-3"><ThemeDropdown theme={currentTheme} onSelect={setTheme} placement="top" triggerClassName="h-11 w-11 rounded-full border border-border-color bg-(--bg) shadow-custom hover:bg-black/6 dark:hover:bg-white/8" /></div>;
    $[25] = currentTheme;
    $[26] = setTheme;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      viewTransitionName: "site-nav-mobile"
    };
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = shouldUseSolidShell && "nav-shell-solid";
  let t21;
  if ($[29] !== t20) {
    t21 = cn("nav-shell flex items-center gap-2 rounded-full px-2 py-2", "border-border-color shadow-custom border", t20);
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== items || $[32] !== pathname) {
    let t23;
    if ($[34] !== pathname) {
      t23 = item_0 => <NavLink key={item_0.link} item={item_0} isActive={pathname === item_0.link} mobile={true} />;
      $[34] = pathname;
      $[35] = t23;
    } else {
      t23 = $[35];
    }
    t22 = items.map(t23);
    $[31] = items;
    $[32] = pathname;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[36] !== t22) {
    t23 = <div className="grid flex-1 grid-cols-4 gap-1">{t22}</div>;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t21 || $[39] !== t23) {
    t24 = <nav aria-label="Primary mobile" style={t19 as React.CSSProperties} className={t21} onMouseMove={handleGlowMove}>{t23}</nav>;
    $[38] = t21;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t18 || $[42] !== t24) {
    t25 = <div className="relative rounded-full">{t18}{t24}</div>;
    $[41] = t18;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== t15 || $[45] !== t17 || $[46] !== t25) {
    t26 = <div style={t15} className={t17}>{t25}</div>;
    $[44] = t15;
    $[45] = t17;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== t12 || $[49] !== t26 || $[50] !== t4) {
    t27 = <>{t4}{t12}{t26}</>;
    $[48] = t12;
    $[49] = t26;
    $[50] = t4;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  return t27;
};
export default NavBar;
function _temp(event) {
  const target = event.currentTarget;
  const rect = target.getBoundingClientRect();
  const x = (event.clientX - rect.left) / rect.width * 100;
  const y = (event.clientY - rect.top) / rect.height * 100;
  target.style.setProperty("--nav-glow-x", `${x}%`);
  target.style.setProperty("--nav-glow-y", `${y}%`);
}